'use client';

import { c as _c } from "react/compiler-runtime";
import { sendGTMEvent } from '@next/third-parties/google';
import { CalendarDays, Check, ChevronsUpDown, MapPin, Phone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/common/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/common/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/common/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/popover';
import { cn } from '@/lib/utils/utils';
import { client } from '@/sanity/lib/client';
import { ALL_LOCATIONS_WITH_BOOKING_QUERY } from '@/sanity/lib/queries/location';
import type { ALL_LOCATIONS_WITH_BOOKING_QUERY_RESULT } from '@/sanity/types';
import { CalendlyScript, getDataLayerAttribution, openCalendlyPopup, type UtmParams, useCalendlyCSS } from './CalendlyLinks';
type BookingLocation = ALL_LOCATIONS_WITH_BOOKING_QUERY_RESULT[number];
type BookingType = 'tour' | 'call';
export default function CalendlyFlowPopup(t0) {
  const $ = _c(55);
  const {
    children
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [locations, setLocations] = useState(t1);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedBookingType, setSelectedBookingType] = useState(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  useCalendlyCSS();
  let t2;
  let t3;
  if ($[1] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      setLoading(true);
      client.fetch(ALL_LOCATIONS_WITH_BOOKING_QUERY).then(data => {
        const filtered = (data ?? []).filter(_temp);
        setLocations(filtered);
      }).finally(() => setLoading(false));
    };
    t3 = [open];
    $[1] = open;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSelectedLocation(null);
      setSelectedBookingType(null);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const reset = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = isOpen => {
      setOpen(isOpen);
      if (!isOpen) {
        reset();
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleOpenChange = t5;
  const hasTour = Boolean(selectedLocation?.tourBooking?.tourBookingUrl);
  const hasCall = Boolean(selectedLocation?.admissionsPartner?.callBookingUrl);
  let t6;
  let t7;
  if ($[6] !== hasCall || $[7] !== hasTour || $[8] !== selectedLocation) {
    t6 = () => {
      if (!selectedLocation) {
        return;
      }
      if (hasTour && !hasCall) {
        setSelectedBookingType("tour");
      } else {
        if (hasCall && !hasTour) {
          setSelectedBookingType("call");
        } else {
          setSelectedBookingType(null);
        }
      }
    };
    t7 = [selectedLocation, hasTour, hasCall];
    $[6] = hasCall;
    $[7] = hasTour;
    $[8] = selectedLocation;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== selectedBookingType || $[12] !== selectedLocation) {
    t8 = () => {
      if (!selectedLocation || !selectedBookingType) {
        return;
      }
      const locationName = selectedLocation.locationName ?? "";
      const dataLayerAttribution = getDataLayerAttribution();
      const utmParams = {
        ...dataLayerAttribution,
        utm_content: locationName
      };
      const url = selectedBookingType === "tour" ? selectedLocation.tourBooking?.tourBookingUrl : selectedLocation.admissionsPartner?.callBookingUrl;
      if (!url) {
        return;
      }
      const bookingLabel = selectedBookingType === "tour" ? "Book a Tour" : "Book a Call";
      openCalendlyPopup(url, utmParams, bookingLabel, locationName);
      sendGTMEvent({
        event: selectedBookingType === "tour" ? "calendly_booking_tour_started" : "calendly_booking_call_started",
        location_name: locationName,
        source: "masthead_calendly_flow"
      });
      setOpen(false);
      reset();
    };
    $[11] = selectedBookingType;
    $[12] = selectedLocation;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleSubmit = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CalendlyScript />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== children) {
    t10 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[15] = children;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogHeader><DialogTitle>Start your journey with N</DialogTitle><DialogDescription>Select a nursery and choose how you'd like to get in touch with us.</DialogDescription></DialogHeader>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <label className="text-sm font-medium">Choose a nursery</label>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== loading || $[20] !== selectedLocation) {
    t13 = selectedLocation ? <span className="flex items-center gap-2"><MapPin className="size-4 shrink-0 text-primary-pink" />{selectedLocation.locationName}</span> : <span className="text-muted-foreground">{loading ? "Loading nurseries..." : "Search for a nursery..."}</span>;
    $[19] = loading;
    $[20] = selectedLocation;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronsUpDown className="size-4 shrink-0 opacity-50" />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t13) {
    t15 = <PopoverTrigger asChild={true}><Button variant="outline" className="w-full justify-between font-normal">{t13}{t14}</Button></PopoverTrigger>;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <CommandInput placeholder="Search nurseries..." />;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <CommandEmpty>No nursery found.</CommandEmpty>;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== locations || $[28] !== selectedLocation?._id) {
    let t19;
    if ($[30] !== selectedLocation?._id) {
      t19 = location => <CommandItem key={location._id} value={location.locationName ?? ""} onSelect={() => {
        setSelectedLocation(location);
        setComboboxOpen(false);
      }}><MapPin className="size-4 shrink-0 text-muted-foreground" />{location.locationName}{selectedLocation?._id === location._id && <Check className="ml-auto size-4" />}</CommandItem>;
      $[30] = selectedLocation?._id;
      $[31] = t19;
    } else {
      t19 = $[31];
    }
    t18 = locations.map(t19);
    $[27] = locations;
    $[28] = selectedLocation?._id;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[32] !== t18) {
    t19 = <PopoverContent className="w-full p-0" align="start"><Command>{t16}<CommandList>{t17}<CommandGroup>{t18}</CommandGroup></CommandList></Command></PopoverContent>;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== comboboxOpen || $[35] !== t15 || $[36] !== t19) {
    t20 = <div className="flex flex-col gap-2">{t12}<Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>{t15}{t19}</Popover></div>;
    $[34] = comboboxOpen;
    $[35] = t15;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== hasCall || $[39] !== hasTour || $[40] !== selectedBookingType || $[41] !== selectedLocation) {
    t21 = selectedLocation && <div className="flex flex-col gap-2"><label className="text-sm font-medium">How would you like to connect?</label><div className="flex gap-3">{hasTour && <button type="button" onClick={() => setSelectedBookingType("tour")} className={cn("flex flex-1 flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors", selectedBookingType === "tour" ? "border-primary-pink bg-pink-50 text-primary-pink" : "border-gray-200 hover:border-gray-300")}><CalendarDays className="size-6" /><span className="text-sm font-medium">Book a Tour</span></button>}{hasCall && <button type="button" onClick={() => setSelectedBookingType("call")} className={cn("flex flex-1 flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors", selectedBookingType === "call" ? "border-primary-pink bg-pink-50 text-primary-pink" : "border-gray-200 hover:border-gray-300")}><Phone className="size-6" /><span className="text-sm font-medium">Book a Call</span></button>}</div></div>;
    $[38] = hasCall;
    $[39] = hasTour;
    $[40] = selectedBookingType;
    $[41] = selectedLocation;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== handleSubmit || $[44] !== selectedBookingType || $[45] !== selectedLocation) {
    t22 = selectedLocation && selectedBookingType && <Button onClick={handleSubmit} className="w-full rounded-full font-bold">{selectedBookingType === "tour" ? "Book a Tour" : "Book a Call"}</Button>;
    $[43] = handleSubmit;
    $[44] = selectedBookingType;
    $[45] = selectedLocation;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t20 || $[48] !== t21 || $[49] !== t22) {
    t23 = <DialogContent className="border-0 shadow-all-sides-md sm:max-w-md">{t11}<div className="flex flex-col gap-5">{t20}{t21}{t22}</div></DialogContent>;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== open || $[52] !== t10 || $[53] !== t23) {
    t24 = <>{t9}<Dialog open={open} onOpenChange={handleOpenChange}>{t10}{t23}</Dialog></>;
    $[51] = open;
    $[52] = t10;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  return t24;
}
function _temp(loc) {
  return loc.tourBooking?.tourBookingUrl || loc.admissionsPartner?.callBookingUrl;
}